import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LocalizedLink as Link, LanguageSwitcher } from 'gatsby-plugin-i18n-l10n';
import { MdMenu } from 'react-icons/md';
import classNames from 'classnames';
import useOverlay from '../../hooks/use-overlay';
import Image from '../Image';
import T from '../Translation';


export const MENU_ELEMENTS = Object.freeze([{
    title: <T id="commons.menu.services.title">Services</T>,
    elements: [
        { title: <T id="commons.menu.services.el.mdr">Managed Detection & Response</T>, link: '/services/managed-detection-and-response/' },
        { title: <T id="commons.menu.services.el.cti">Cyber Threat Intelligence</T>, link: '/services/cyber-threat-intelligence/' },
        { title: <T id="commons.menu.services.el.ir">Rapid Incident Response</T>, link: '/services/rapid-incident-response/' },
    ],
}, {
    title: <T id="commons.menu.platform.title">Platform</T>,
    elements: [
        { title: <T id="commons.menu.platform.el.secops">SecOps Platform</T>, link: '/platform/security-operations-platform/' },
        { title: <T id="commons.menu.platform.el.detection">Detection Modules</T>, link: '/platform/detection-modules/' },
        { title: <T id="commons.menu.platform.el.response">Response Modules</T>, link: '/platform/response-modules/' },
        { title: <T id="commons.menu.platform.el.ti">Threat Intelligence Modules</T>, link: '/platform/threat-intelligence-modules/' },
    ],
}, {
    title: <T id="commons.menu.resources.title">Resources</T>,
    elements: [
        { title: <T id="commons.menu.resources.el.blog">Blog</T>, link: '/blog/' },
        { title: <T id="commons.menu.resources.el.events">Events & Webinars</T>, link: '/resources/events-and-webinars/' },
        { title: <T id="commons.menu.resources.el.datasheets">Datasheets & Whitepapers</T>, link: '/resources/datasheets-and-whitepapers/' },
    ],
}, {
    title: <T id="commons.menu.company.title">Company</T>,
    elements: [
        { title: <T id="commons.menu.company.el.about-us">About Us</T>, link: '/company/about-us/' },
        { title: <T id="commons.menu.company.el.secops-team">SecOps Team</T>, link: '/company/security-operations-team/' },
        { title: <T id="commons.menu.company.el.news">News</T>, link: '/company/news/' },
        { title: <T id="commons.menu.company.el.partners">Partners</T>, link: '/company/partners/' },
        { title: <T id="commons.menu.company.el.careers">Careers</T>, link: '/company/careers/' },
        { title: <T id="commons.menu.company.el.contact-us">Contact Us</T>, link: '/company/contact-us/' },
    ],
},
]);

function MenuElement(props) {
    const {
        menuBackground, selectionColor, title, elements, selected,
    } = props;
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [isOpen, setIsOpen] = useOverlay(referenceElement, popperElement);

    return (
        <li className="lg:ml-6 max-lg:my-3 relative">
            <button
                type="button"
                className={classNames('hover:text-certego-blue', (isOpen || selected) && (selectionColor || '!text-certego-black'))}
                ref={setReferenceElement}
                onClick={() => setIsOpen(true)}
            >
                {title}
            </button>
            <div
                ref={setPopperElement}
                className={classNames(
                    'rounded w-[200px] absolute',
                    !isOpen && 'hidden',
                    'left-0 top-10',
                    'max-lg:-left-[210px] max-lg:-top-0',
                    'z-10',
                    menuBackground,
                )}
            >
                {elements.map(({ title: t, link }) => (
                    <Link to={link} className={classNames('py-2 px-2 block', 'hover:text-certego-blue')} key={t.props.id}>
                        {t}
                    </Link>
                ))}
            </div>
        </li>
    );
}

MenuElement.defaultProps = {
    menuBackground: null,
    elements: [],
    selected: false,
    selectionColor: null,
    title: null,
};

MenuElement.propTypes = {
    menuBackground: PropTypes.string,
    elements: PropTypes.array,
    selected: PropTypes.bool,
    selectionColor: PropTypes.string,
    title: PropTypes.node,
};

export default function Menu(props) {
    const { selected, white } = props;
    const [referenceElement, setReferenceElement] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [isOpen, setIsOpen] = useOverlay(referenceElement, popperElement);
    const menuBackgroundSolid = white ? 'bg-certego-white lg:bg-certego-white/95' : 'bg-certego-darkgray lg:bg-certego-darkgray/95';
    const menuBackgroundTransparent = white ? 'bg-certego-white/80' : 'bg-certego-darkgray/80';
    const mainBarOptionsBackground = white ? '[&_option]:bg-certego-white' : '[&_option]:bg-certego-darkgray';
    const mainBarBackground = classNames(menuBackgroundTransparent, 'backdrop-blur-sm', mainBarOptionsBackground);
    const firstMenuBackground = classNames(menuBackgroundSolid, 'backdrop-blur-sm');    // Note: actually on chromium 'backdrop-filter' works only on first element (https://bugs.chromium.org/p/chromium/issues/detail?id=993644)
    const secondMenuBackground = classNames(menuBackgroundSolid, 'backdrop-blur-sm');
    const textColor = white ? 'text-certego-darkgray' : 'text-certego-white';
    const selectionColor = white ? '!text-certego-black' : '!text-certego-gray';

    return (
        <nav className={classNames('w-full rounded-full px-2 py-1', mainBarBackground, textColor)}>
            <div className="flex flex-wrap items-center justify-between mx-auto relative">
                <Link to="/" className="w-[120px] min-h-[34px] mt-2">
                    <Image
                        img={white ? 'logo_black.svg' : 'logo.svg'}
                        className="inline-block"
                    />
                </Link>
                <button
                    type="button"
                    ref={setReferenceElement}
                    onClick={() => setIsOpen(!isOpen)}
                    className="lg:hidden p-2 text-3xl rounded-lg hover:bg-certego-gray"
                >
                    <MdMenu />
                </button>
                <div
                    ref={setPopperElement}
                    className={classNames(
                        'lg:grow',
                        'max-lg:absolute max-lg:right-0 max-lg:top-full max-lg:rounded max-lg:px-6 max-lg:py-3',
                        isOpen && firstMenuBackground,
                        isOpen ? 'max-lg:block' : 'max-lg:hidden',
                    )}
                >
                    <ul className="lg:pl-12 flex flex-col lg:flex-row w-full">
                        {MENU_ELEMENTS.map(({ title, elements }) => (
                            <MenuElement
                                title={title}
                                selected={(selected?.props?.id || selected) === (title?.props?.id || title)}
                                elements={elements}
                                key={title.props.id}
                                selectionColor={selectionColor}
                                menuBackground={secondMenuBackground}
                            />
                        ))}
                        <li className="lg:ml-auto lg:mr-5 max-lg:my-3 relative">
                            <LanguageSwitcher resolveLanguageName={(l) => ({ 'it-IT': '🇮🇹', 'en-GB': '🇬🇧' }[l])} />
                        </li>
                    </ul>
                </div>
                <Link
                    to="/have-you-been-breached"
                    className="max-xl:hidden rounded-full bg-red-700 font-semibold py-1 px-2 text-certego-white hover:text-certego-gray mr-3"
                >
                    <T id="have-you-been-breached.title">Are you under attack?</T>
                </Link>
            </div>
        </nav>
    );
}

Menu.defaultProps = {
    selected: null,
    white: false,
};

Menu.propTypes = {
    selected: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.node,
    ]),
    white: PropTypes.bool,
};
